import { useEffect, useState } from "react";
import { FaPencilAlt, FaTrashAlt, FaExclamationTriangle, FaReceipt } from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/campamentos";

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Las fechas llegan como YYYY-MM-DD (sin zona horaria): se formatean sin pasar por Date.
const formatoFecha = (fecha) => {
  if (!fecha) return "";
  const [anio, mes, dia] = fecha.split("-");
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${dia} ${meses[Number(mes) - 1]} ${anio}`;
};

const hoyMexico = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(new Date());

const clasesInput =
  "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none";
const clasesBotonPrimario =
  "px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50";
const clasesBotonSecundario =
  "px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-50";

const formVacio = () => ({ fecha: hoyMexico(), concepto: "", monto: "" });

async function llamar(ruta, { method = "GET", cuerpo } = {}) {
  try {
    const res = await fetch(`${API}${ruta}`, {
      method,
      credentials: "include",
      headers: cuerpo !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data, mensaje: data.mensaje || (res.ok ? "" : "Ocurrió un error") };
  } catch (error) {
    console.error(error);
    return { ok: false, data: {}, mensaje: "No se pudo conectar con el servidor" };
  }
}

// Gastos de un campamento: fecha, concepto (texto libre) y monto. Quitar un gasto lo deja inactivo.
export default function CampamentoGastosModal({ open, campamento, onClose, onChanged }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(formVacio);
  const [editando, setEditando] = useState(null);
  const [aQuitar, setAQuitar] = useState(null);

  const idCampamento = campamento?.id_campamento;
  const inactivo = datos ? !datos.activo : !campamento?.activo;

  useEffect(() => {
    if (!open || !idCampamento) return;
    setDatos(null);
    setError("");
    setEditando(null);
    setForm(formVacio());
    setCargando(true);
    llamar(`/${idCampamento}/gastos`).then((r) => {
      if (r.ok) setDatos(r.data);
      else setError(r.mensaje);
      setCargando(false);
    });
  }, [open, idCampamento]);

  const limpiar = () => { setEditando(null); setForm(formVacio()); };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.fecha) return setError("Escribe la fecha del gasto");
    if (!form.concepto.trim()) return setError("Escribe el concepto del gasto");
    if (!(Number(form.monto) > 0)) return setError("Escribe el monto del gasto");

    setGuardando(true);
    const cuerpo = { fecha: form.fecha, concepto: form.concepto.trim(), monto: form.monto };
    const r = editando
      ? await llamar(`/gastos/${editando}`, { method: "PUT", cuerpo })
      : await llamar(`/${idCampamento}/gastos`, { method: "POST", cuerpo });
    setGuardando(false);
    if (!r.ok) return setError(r.mensaje);
    setDatos(r.data);
    limpiar();
    onChanged?.();
  };

  const editar = (g) => {
    setError("");
    setEditando(g.id_gasto);
    setForm({ fecha: g.fecha, concepto: g.concepto, monto: String(g.monto) });
  };

  const quitar = async () => {
    const r = await llamar(`/gastos/${aQuitar.id_gasto}`, { method: "DELETE" });
    setAQuitar(null);
    if (!r.ok) return setError(r.mensaje);
    setDatos(r.data);
    if (editando === aQuitar.id_gasto) limpiar();
    onChanged?.();
  };

  const gastos = datos?.gastos || [];

  return (
    <>
      <Modal open={open} onClose={onClose} title={`Gastos — ${campamento?.nombre || ""}`} maxWidth="max-w-3xl">
        {/* FORMULARIO */}
        {!inactivo && (
          <form onSubmit={guardar} className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
            <h4 className="text-xs font-semibold uppercase text-slate-500 mb-3">
              {editando ? "Modificar gasto" : "Registrar gasto"}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr_140px] gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Fecha</label>
                <input type="date" max={hoyMexico()} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={clasesInput} />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Concepto</label>
                <input
                  type="text"
                  maxLength={200}
                  value={form.concepto}
                  onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                  placeholder="Ej. Hospedaje, transporte, playeras..."
                  className={clasesInput}
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Monto</label>
                <input type="number" min="0" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} placeholder="0.00" className={clasesInput} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              {editando && (
                <button type="button" onClick={limpiar} className={clasesBotonSecundario}>Cancelar</button>
              )}
              <button type="submit" disabled={guardando} className={clasesBotonPrimario}>
                {guardando ? "Guardando..." : editando ? "Guardar cambios" : "Agregar gasto"}
              </button>
            </div>
          </form>
        )}

        {inactivo && datos && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            El campamento está inactivo: puedes consultar sus gastos, pero no modificarlos.
          </p>
        )}

        {error && (
          <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-4">{error}</p>
        )}

        {/* LISTA */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold">Fecha</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Concepto</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Monto</th>
                  {!inactivo && <th className="px-4 py-2.5 text-right font-semibold">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {cargando ? (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-400">Cargando...</td></tr>
                ) : gastos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-slate-400">
                      <FaReceipt className="mx-auto mb-2 text-slate-300" />
                      Este campamento todavía no tiene gastos registrados
                    </td>
                  </tr>
                ) : (
                  gastos.map((g) => (
                    <tr key={g.id_gasto} className={`border-t border-slate-100 ${editando === g.id_gasto ? "bg-indigo-50/60" : ""}`}>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{formatoFecha(g.fecha)}</td>
                      <td className="px-4 py-2.5 text-slate-800">{g.concepto}</td>
                      <td className="px-4 py-2.5 text-right text-slate-800 tabular-nums whitespace-nowrap">{formatoMoneda(g.monto)}</td>
                      {!inactivo && (
                        <td className="px-4 py-2.5">
                          <div className="flex justify-end gap-1">
                            <button type="button" onClick={() => editar(g)} aria-label="Modificar gasto" title="Modificar" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:bg-indigo-50 hover:text-indigo-600">
                              <FaPencilAlt className="text-xs" />
                            </button>
                            <button type="button" onClick={() => setAQuitar(g)} aria-label="Quitar gasto" title="Quitar" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                              <FaTrashAlt className="text-xs" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
              {gastos.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                  <tr>
                    <td className="px-4 py-3 text-slate-600" colSpan={2}>
                      Total ({gastos.length} {gastos.length === 1 ? "gasto" : "gastos"})
                    </td>
                    <td className="px-4 py-3 text-right text-rose-600 tabular-nums whitespace-nowrap">{formatoMoneda(datos.total)}</td>
                    {!inactivo && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <button type="button" onClick={onClose} className={clasesBotonSecundario}>Cerrar</button>
        </div>
      </Modal>

      {/* CONFIRMAR QUITAR */}
      <Modal open={!!aQuitar} onClose={() => setAQuitar(null)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-rose-50 text-rose-500">
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Quitar gasto</h3>
          <p className="text-sm text-slate-500 mb-6">
            ¿Quitar "{aQuitar?.concepto}" por {aQuitar && formatoMoneda(aQuitar.monto)}? Dejará de contar en el total y queda anotado en el historial.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setAQuitar(null)} className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
            <button onClick={quitar} className="w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition bg-rose-600 hover:bg-rose-700">Quitar gasto</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
